import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle, Shield, Clock, MapPin } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

interface SecurityPattern {
  user_id: string;
  pattern_type: string;
  event_count: number;
  first_event: string;
  last_event: string;
  details: any; // JSONB from database
}

const SecurityMonitoring = () => {
  const [patterns, setPatterns] = useState<SecurityPattern[]>([]);
  const [loading, setLoading] = useState(true);
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }

    const fetchPatterns = async () => {
      const { data, error } = await supabase
        .rpc('detect_unusual_auth_patterns');

      if (!error && data) {
        setPatterns(data);
      }
      setLoading(false);
    };

    fetchPatterns();

    // Refresh every 30 seconds
    const interval = setInterval(fetchPatterns, 30000);
    return () => clearInterval(interval);
  }, [isAdmin, navigate]);

  const getPatternSeverity = (type: string): "destructive" | "default" | "secondary" => {
    switch (type) {
      case 'multiple_failed_logins':
        return 'destructive';
      case 'admin_role_change':
        return 'destructive';
      case 'multiple_ip_logins':
        return 'default';
      default:
        return 'secondary';
    }
  };

  const getPatternTitle = (type: string): string => {
    switch (type) {
      case 'multiple_failed_logins':
        return 'Multiple Failed Login Attempts';
      case 'admin_role_change':
        return 'Admin Role Change Detected';
      case 'multiple_ip_logins':
        return 'Multiple IP Address Logins';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <p>Loading security monitoring...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-3xl font-bold">Security Monitoring</h1>
              <p className="text-muted-foreground">
                Real-time authentication security alerts
              </p>
            </div>
          </div>

          {patterns.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <Shield className="h-16 w-16 mx-auto text-green-500 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">All Clear</h3>
                  <p className="text-muted-foreground">
                    No unusual authentication patterns detected
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {patterns.map((pattern, index) => (
                <Alert key={index} variant={getPatternSeverity(pattern.pattern_type) === 'destructive' ? 'destructive' : 'default'}>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle className="flex items-center gap-2">
                    {getPatternTitle(pattern.pattern_type)}
                    <Badge variant={getPatternSeverity(pattern.pattern_type)}>
                      {pattern.event_count} events
                    </Badge>
                  </AlertTitle>
                  <AlertDescription>
                    <div className="mt-2 space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        <span>
                          First: {new Date(pattern.first_event).toLocaleString()} | 
                          Last: {new Date(pattern.last_event).toLocaleString()}
                        </span>
                      </div>
                      
                      {pattern.details.unique_ips && (
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 mt-0.5" />
                          <div>
                            <p>Unique IPs: {pattern.details.unique_ips}</p>
                            {pattern.details.ip_addresses && pattern.details.ip_addresses.length > 0 && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {pattern.details.ip_addresses.join(', ')}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {pattern.details.changes && (
                        <div className="mt-2">
                          <p className="font-medium">Role changes:</p>
                          <pre className="text-xs mt-1 p-2 bg-muted rounded">
                            {JSON.stringify(pattern.details.changes, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          )}

          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Monitoring Information</CardTitle>
              <CardDescription>
                How authentication security monitoring works
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <h4 className="font-semibold mb-1">Multiple Failed Logins</h4>
                <p className="text-muted-foreground">
                  Triggers when 5+ failed login attempts detected within 1 hour. May indicate brute force attack.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-1">Multiple IP Logins</h4>
                <p className="text-muted-foreground">
                  Triggers when successful logins from 3+ different IP addresses within 1 hour. May indicate account compromise.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-1">Admin Role Changes</h4>
                <p className="text-muted-foreground">
                  Tracks all admin role assignments within the last 24 hours for audit purposes.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SecurityMonitoring;
